import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Users, Shield, Award, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navigation from "@/components/Navigation";
import FloatingUserMenu from "@/components/FloatingUserMenu";
import AdminMapEditor from "@/components/AdminMapEditor";
import AdminPackageEditor from "@/components/AdminPackageEditor";
import AdminTimelineEditor from "@/components/AdminTimelineEditor";
import UsersManagement from "@/components/admin/UsersManagement";
import ReviewsModeration from "@/components/admin/ReviewsModeration";
import InviteManagement from "@/components/admin/InviteManagement";
import DataExportImport from "@/components/admin/DataExportImport";
import DiveSiteReportsManagement from "@/components/admin/DiveSiteReportsManagement";
import DiveSitesManagement from "@/components/admin/DiveSitesManagement";
import DiveReportsManagement from "@/components/admin/DiveReportsManagement";
import AdminTabNav from "@/components/admin/AdminTabNav";
import AdminStatsCard from "@/components/admin/AdminStatsCard";
import ThemeManager from "@/components/admin/ThemeManager";
import AnnouncementManager from "@/components/admin/AnnouncementManager";
import AccountAnnouncementManager from "@/components/admin/AccountAnnouncementManager";
import NewsletterManager from "@/components/admin/NewsletterManager";
import NotificationSettings from "@/components/admin/NotificationSettings";
import BadgeManager from "@/components/admin/BadgeManager";
import CommunityProjectsManager from "@/components/admin/CommunityProjectsManager";
import TripManager from "@/components/admin/TripManager";
import DiveCenterAccountsManager from "@/components/admin/DiveCenterAccountsManager";
import LocationsManagement from "@/components/admin/LocationsManagement";
import ChatForumModeration from "@/components/admin/ChatForumModeration";
import CloudStorageManager from "@/components/admin/CloudStorageManager";
import SEO from "@/components/SEO";
import { MapPin, Waves, Map } from "lucide-react";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

// Sub-tab component for Locations & Map
const LocationsMapTab = () => {
  const [subTab, setSubTab] = useState('locations');
  
  const subTabs = [
    { id: 'locations', label: 'Locations & Centers', icon: MapPin },
    { id: 'dive-sites', label: 'Dive Sites', icon: Waves },
    { id: 'map-editor', label: 'Map Editor', icon: Map },
  ];

  return (
    <div>
      <div className="flex gap-2 mb-6 border-b border-gray-200 pb-3">
        {subTabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setSubTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2 transition-colors ${
                subTab === tab.id
                  ? 'bg-cyan-100 text-cyan-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid={`subtab-${tab.id}`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>
      {subTab === 'locations' && <LocationsManagement />}
      {subTab === 'dive-sites' && <DiveSitesManagement />}
      {subTab === 'map-editor' && <AdminMapEditor />}
    </div>
  );
};

const Admin = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'users'); // 'users', 'map', 'packages', 'reviews', 'invites', 'cloud-storage'...
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [diveSiteReports, setDiveSiteReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);

  const user = JSON.parse(localStorage.getItem("user") || "null");
  const token = localStorage.getItem("token");

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (err) {
      setError("Failed to load users");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    // Check if user is admin
    if (!user || user.role !== "admin") {
      navigate("/account");
      return;
    }

    fetchUsers();
  }, [user, navigate, fetchUsers]);

  // Fetch reviews when reviews tab is active
  useEffect(() => {
    if (activeTab === 'reviews') {
      fetchReviews();
    }
    if (activeTab === 'invites') {
      fetchInvitations();
    }
    if (activeTab === 'reports') {
      fetchDiveSiteReports();
    }
  }, [activeTab, token]);

  const fetchReviews = async () => {
    try {
      setReviewsLoading(true);
      const response = await axios.get(`${API}/reviews/all`);
      setReviews(response.data);
    } catch (err) {
      console.error("Failed to load reviews:", err);
    } finally {
      setReviewsLoading(false);
    }
  };

  const fetchDiveSiteReports = async () => {
    try {
      setReportsLoading(true);
      const response = await axios.get(`${API}/admin/dive-site-reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiveSiteReports(response.data);
    } catch (err) {
      console.error("Failed to load dive site reports:", err);
    } finally {
      setReportsLoading(false);
    }
  };

  const fetchInvitations = async () => {
    try {
      setInvitationsLoading(true);
      const response = await axios.get(`${API}/admin/invitations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvitations(response.data);
    } catch (err) {
      console.error("Failed to load invitations:", err);
    } finally {
      setInvitationsLoading(false);
    }
  };

  if (!user || user.role !== "admin") {
    return null;
  }

  const roleCount = {
    total: users.length,
    admin: users.filter(u => u.role === "admin").length,
    pro: users.filter(u => u.role === "pro").length,
    member: users.filter(u => u.role === "member").length
  };

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      <SEO 
        title="Admin Dashboard - George Dives"
        description="Admin dashboard for managing George Dives"
      />
      
      <Navigation />
      <FloatingUserMenu />

      <section className="pt-32 pb-16 bg-gradient-to-br from-red-500 to-pink-600">
        <div className="container mx-auto px-4 max-w-7xl">
          <Button
            onClick={() => navigate("/account")}
            variant="outline"
            className="mb-6 border-white text-white hover:bg-white/20"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Account
          </Button>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Admin Dashboard
          </h1>
          <p className="text-lg text-white/90">
            Manage users and site content
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Tab Navigation */}
          <AdminTabNav activeTab={activeTab} setActiveTab={setActiveTab} />

          {/* Stats Cards */}
          {activeTab === 'users' && (
            <div className="grid md:grid-cols-4 gap-6 mb-12">
              <AdminStatsCard 
                title="Total Users" 
                value={roleCount.total} 
                icon={Users} 
                iconColor="text-gray-600"
                valueColor="text-cyan-600"
              />
              <AdminStatsCard 
                title="Admins" 
                value={roleCount.admin} 
                icon={Shield} 
                iconColor="text-red-600"
                valueColor="text-red-600"
              />
              <AdminStatsCard 
                title="Pros" 
                value={roleCount.pro} 
                icon={Award} 
                iconColor="text-purple-600"
                valueColor="text-purple-600"
              />
              <AdminStatsCard 
                title="Members" 
                value={roleCount.member} 
                icon={Users} 
                iconColor="text-blue-600"
                valueColor="text-blue-600"
              />
            </div>
          )}

          {/* Locations & Map Tab - with sub-tabs */}
          {activeTab === 'locations-map' && (
            <LocationsMapTab />
          )}

          {/* Timeline Editor Tab */}
          {activeTab === 'timeline' && (
            <AdminTimelineEditor />
          )}

          {/* Package Editor Tab */}
          {activeTab === 'packages' && (
            <AdminPackageEditor />
          )}

          {/* Users Management Tab */}
          {activeTab === 'users' && (
            <UsersManagement 
              users={users}
              loading={loading}
              error={error}
              token={token}
              currentUser={user}
              onUsersUpdate={setUsers}
            />
          )}

          {/* Dive Center Accounts Tab */}
          {activeTab === 'dive-centers' && (
            <DiveCenterAccountsManager token={token} />
          )}

          {/* Reviews Moderation Tab */}
          {activeTab === 'reviews' && (
            <ReviewsModeration
              reviews={reviews}
              loading={reviewsLoading}
              token={token}
              onReviewsUpdate={setReviews}
            />
          )}

          {/* Invite Management Tab */}
          {activeTab === 'invites' && (
            <InviteManagement
              invitations={invitations}
              loading={invitationsLoading}
              token={token}
              onInvitationsUpdate={setInvitations}
              onFetchInvitations={fetchInvitations}
            />
          )}

          {/* Dive Reports Management Tab (New System) */}
          {activeTab === 'reports' && (
            <DiveReportsManagement token={token} />
          )}

          {/* Data Export/Import Tab */}
          {activeTab === 'export' && (
            <DataExportImport token={token} />
          )}

          {/* Announcements Tab */}
          {activeTab === 'announcements' && (
            <>
              <AnnouncementManager token={token} />
              <AccountAnnouncementManager token={token} />
            </>
          )}

          {/* Newsletter Tab */}
          {activeTab === 'newsletter' && (
            <NewsletterManager token={token} />
          )}

          {/* Badges Tab */}
          {activeTab === 'badges' && (
            <BadgeManager token={token} users={users} />
          )}

          {/* Community Projects Tab */}
          {activeTab === 'community' && (
            <CommunityProjectsManager token={token} />
          )}

          {/* Trip Management Tab */}
          {activeTab === 'trips' && (
            <TripManager token={token} />
          )}

          {/* Chat/Forum Moderation Tab */}
          {activeTab === 'chat-forum' && (
            <ChatForumModeration token={token} />
          )}

          {/* Notification Settings Tab */}
          {activeTab === 'notifications' && (
            <NotificationSettings token={token} />
          )}

          {/* Theme Manager Tab */}
          {activeTab === 'theme' && (
            <ThemeManager />
          )}

          {/* Cloud Storage Tab */}
          {activeTab === 'cloud-storage' && (
            <CloudStorageManager token={token} />
          )}
        </div>
      </section>
    </div>
  );
};

export default Admin;
